import { request } from './client'

/* Serviceable areas — the half of housekeeping with no guest in it.

   NOTE WHAT THE SERVER SENDS AND WHY IT IS AN EXCEPTION. Everywhere else in this product
   a derived value is derived where it is drawn. `standing` and `latenessRatio` are not:
   they depend on the PROPERTY'S LOCAL TIME OF DAY — a restaurant cleaned between services
   is resting at three in the morning in Najaf, not at three in the morning in the
   browser's time zone — so a client in another country would compute them differently.

   Everything they were derived from is sent too, so a screen can show its working and a
   defect in the rule is visible rather than only wrong. */

export type AreaKind =
  | 'FRONT_OF_HOUSE' | 'CORRIDOR'
  | 'DINING' | 'FUNCTION' | 'BACK_OF_HOUSE'

/**
 * OnSchedule · DueSoon · Overdue · Missed · BeingCleaned · Closed · Resting.
 *
 * Seven, not two, because an area ten minutes short of its next clean is completely fine
 * and one two hours past it is a complaint waiting to happen — and the middle of that
 * range is the useful part a boolean throws away. Three of the seven mean "not late, and
 * for a different reason each time", which is what stops a board painting a shut function
 * room the same colour as a neglected corridor.
 */
export type AreaStanding =
  | 'ON_SCHEDULE' | 'DUE_SOON' | 'OVERDUE' | 'MISSED'
  | 'BEING_CLEANED' | 'CLOSED' | 'RESTING'

export interface AreaBoardRow {
  areaId: string
  code: string
  name: string
  nameAr: string | null
  kind: AreaKind
  zoneId: string | null
  zoneName: string | null
  zoneNameAr: string | null
  /** Minutes between cleans. 90 for a washroom, 10080 for a weekly deep clean. */
  intervalMinutes: number
  /** The active window, as minutes past local midnight. Equal values mean around the clock. */
  activeFromMinute: number
  activeToMinute: number
  lastCompletedUtc: string | null
  service: 'IN_SERVICE' | 'OUT_OF_SERVICE' | 'OUT_OF_ORDER'
  standing: AreaStanding
  /**
   * Lateness as a FRACTION of the interval: 0 is due now, 1 is a whole interval missed,
   * negative is not yet due. Null where the standing suppresses lateness — closed, being
   * cleaned, resting, or never cleaned at all.
   *
   * THIS IS FOR THE MACHINE, NOT THE READER. It exists so one list can sort a
   * ninety-minute round against a weekly deep clean. A screen showing "1.5" to a
   * supervisor has shown them nothing; multiply it by the interval and say "three hours
   * late", which is a fact they can act on.
   */
  latenessRatio: number | null
  nextDueUtc: string | null
}

export function fetchAreas(
  propertyId: string, options: { signal?: AbortSignal } = {},
): Promise<AreaBoardRow[]> {
  return request<AreaBoardRow[]>(
    `/properties/${propertyId}/areas`,
    options.signal ? { signal: options.signal } : {})
}

/**
 * Somebody cleaned it. ONE COLUMN MOVES — the completion time — and the standing follows
 * from it. There is no status to set, which is the whole point of the model.
 */
export function recordAreaClean(
  propertyId: string,
  areaId: string,
  body: { capturedAtUtc?: string; note?: string } = {},
): Promise<AreaBoardRow> {
  return request<AreaBoardRow>(
    `/properties/${propertyId}/areas/${areaId}/clean`,
    { method: 'POST', body })
}

/**
 * **CLOSING A PUBLIC AREA BY HAND, AND REOPENING IT.** Sprint 57.
 *
 * `area.setOutOfService` was granted to the supervisor from the first seed and nothing in
 * the product performed it for thirty sprints. A reason is required both ways — reopening
 * needs it more, because the area goes straight back onto the schedule.
 *
 * Returns the WHOLE ROW, not a boolean: closing an area erases its lateness ratio, and a
 * screen patching one field would go on drawing "four hours overdue" beside "closed".
 */
export function setAreaService(
  propertyId: string,
  areaId: string,
  body: { outOfService: boolean; reason: string },
): Promise<AreaBoardRow> {
  return request<AreaBoardRow>(
    `/properties/${propertyId}/areas/${areaId}/service`,
    { method: 'POST', body })
}

/**
 * HOW LATE, IN MINUTES, for a person to read.
 *
 * The ratio sorts the board; this is what goes on the row. Returns null where lateness
 * does not apply, so a caller cannot accidentally render "0 minutes late" over a shut
 * function room.
 */
export function minutesLate(row: AreaBoardRow): number | null {
  return row.latenessRatio === null || row.latenessRatio < 0
    ? null
    : Math.round(row.latenessRatio * row.intervalMinutes)
}
